import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  TextField,
  Button,
  InputAdornment,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReportPdfLayout from "../../../Components/ReportPdfLayout";

const formatDate = (d) => {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString();
};

const DoctorActivityReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const today = new Date();
  const defaultEnd = new Date(today);
  const defaultStart = new Date(today);
  defaultStart.setDate(defaultStart.getDate() - 30);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [expandedDoctor, setExpandedDoctor] = useState(null);
  const [doctorSearch, setDoctorSearch] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.append("start_date", startDate.toISOString().slice(0, 10));
    p.append("end_date", endDate.toISOString().slice(0, 10));
    return p.toString();
  }, [startDate, endDate]);

  const endpoint = `reports/doctor_activity/?${params}`;
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["reports-doctor-activity", params],
    endpoint,
    token
  );

  const handleApply = () => refetch();
  const toggleExpand = (id) =>
    setExpandedDoctor((prev) => (prev === id ? null : id));

  const allDoctors = data?.doctors ?? [];
  const filteredDoctors = useMemo(() => {
    const q = (doctorSearch || "").trim().toLowerCase();
    if (!q) return allDoctors;
    return allDoctors.filter(
      (d) => (d.name || "").toLowerCase().includes(q)
    );
  }, [allDoctors, doctorSearch]);
  const doctors = filteredDoctors;
  const dateRange = data?.date_range ?? {};
  const totalPatients = doctors.reduce((sum, d) => sum + (d.patient_count || 0), 0);
  const totalPatientsAll = allDoctors.reduce((sum, d) => sum + (d.patient_count || 0), 0);

  return (
    <Grid container maxWidth="xl" sx={{ p: 3, width: "100%" }}>
      <Grid item xs={12}>
        <ReportPdfLayout
          reportTitle={t("reports.doctorActivityReport")}
          fileName="doctor-activity-report.pdf"
          disabled={isLoading || isError || !data}
        >
          <Grid container>
          <Grid item xs={12} className="no-print">
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {t("reports.doctorActivityReport")}
            </Typography>
          </Grid>

          <Grid item xs={12} mb={2} className="no-print">
            <Paper sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <DatePicker
            selected={startDate}
            onChange={(d) => setStartDate(d)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            customInput={
              <TextField size="small" label={t("reports.startDate")} sx={{ minWidth: 160 }} />
            }
          />
          <DatePicker
            selected={endDate}
            onChange={(d) => setEndDate(d)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            customInput={
              <TextField size="small" label={t("reports.endDate")} sx={{ minWidth: 160 }} />
            }
          />
          <Button variant="contained" onClick={handleApply}>
            {t("reports.apply")}
          </Button>
            </Paper>
          </Grid>

          {!isLoading && !isError && data && allDoctors.length > 0 && (
            <Grid item xs={12} mb={2} className="no-print">
              <TextField
            size="small"
            placeholder={t("reports.searchDoctor")}
            value={doctorSearch}
            onChange={(e) => setDoctorSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
              />
            </Grid>
          )}

              {isError && (
            <Grid item xs={12}>
              <Alert severity="error">{error?.message || t("reports.errorLoading")}</Alert>
            </Grid>
          )}

          {isLoading && (
            <Grid item xs={12} display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Grid>
          )}

          {!isLoading && !isError && data && (
        <>
          <Grid item xs={12} mb={2}>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Typography variant="body1">
                {t("reports.dateRange")}: {dateRange.start} — {dateRange.end}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {doctorSearch.trim()
                  ? t("reports.showingDoctors", { count: doctors.length, total: allDoctors.length })
                  : t("reports.totalDoctors") + ": " + doctors.length}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {t("reports.totalPatients")}: {totalPatients}
                {doctorSearch.trim() && totalPatients !== totalPatientsAll && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({t("reports.ofTotal")} {totalPatientsAll})
                  </Typography>
                )}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                    <TableCell sx={{ color: "white", fontWeight: 600 }} />
                    <TableCell sx={{ color: "white", fontWeight: 600 }}>
                      {t("reports.doctorName")}
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                      {t("reports.patientCount")}
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                      {t("reports.activityCount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {doctors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        {doctorSearch.trim()
                          ? t("reports.noDoctorsMatch")
                          : t("reports.noDoctorsInRange")}
                      </TableCell>
                    </TableRow>
                  ) : (
                  doctors.map((doc) => (
                    <React.Fragment key={doc.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpand(doc.id)}
                            aria-label={expandedDoctor === doc.id ? "collapse" : "expand"}
                          >
                            {expandedDoctor === doc.id ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{doc.name || "—"}</TableCell>
                        <TableCell align="right">{doc.patient_count ?? 0}</TableCell>
                        <TableCell align="right">{doc.activity_count ?? 0}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 0 }}>
                          <Collapse in={expandedDoctor === doc.id} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {t("reports.activities")}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t("reports.patientName")}</TableCell>
                                    <TableCell>{t("reports.ticketNumber")}</TableCell>
                                    <TableCell>{t("reports.date")}</TableCell>
                                    <TableCell>{t("reports.type")}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(doc.activities || []).map((act, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{act.patient_name || "—"}</TableCell>
                                      <TableCell>{act.ticket_number || "—"}</TableCell>
                                      <TableCell>{formatDate(act.date)}</TableCell>
                                      <TableCell>{act.type || "—"}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
          )}
          </Grid>
        </ReportPdfLayout>
      </Grid>
    </Grid>
  );
};

export default DoctorActivityReport;
